import {
  CalendarPlus,
  Storefront,
  UserPlus,
  Package,
  UsersThree,
  ChartLineUp,
  Gear,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { DashboardLabels, DashboardQuickAction, DashboardQuickActionIcon } from "./types";

const ICON_MAP: Record<DashboardQuickActionIcon, PhosphorIcon> = {
  calendar: CalendarPlus,
  cashier: Storefront,
  customers: UserPlus,
  inventory: Package,
  staff: UsersThree,
  reports: ChartLineUp,
  settings: Gear,
};

export interface DashboardQuickActionGridProps {
  actions: DashboardQuickAction[];
  labels: DashboardLabels["quickActions"];
  onAction?: (action: DashboardQuickAction) => void;
  className?: string;
}

export function DashboardQuickActionGrid({
  actions,
  labels,
  onAction,
  className,
}: DashboardQuickActionGridProps) {
  return (
    <section
      className={cn(
        "rounded-biz-card border border-biz-border bg-biz-surface-elevated p-4 biz-shadow-sm",
        className,
      )}
      aria-labelledby="dash-qa-title"
    >
      <h3 id="dash-qa-title" className="mb-3 text-sm font-semibold text-biz-foreground">
        {labels.title}
      </h3>
      <div
        className={cn(
          "grid gap-2",
          "grid-cols-3 sm:grid-cols-6",
        )}
      >
        {actions.map((a) => {
          const Icon = ICON_MAP[a.icon] ?? CalendarPlus;
          const label = labels.items[a.labelKey] ?? a.labelKey;
          return (
            <button
              key={a.key}
              type="button"
              onClick={() => onAction?.(a)}
              className={cn(
                "group flex min-h-[88px] flex-col items-center justify-center gap-1.5 rounded-biz-btn border border-biz-border bg-biz-surface px-2 py-3 text-center transition-all duration-180",
                "hover:bg-biz-muted/60 hover:border-biz-border-strong",
                "active:scale-[0.96]",
                "biz-focus-ring biz-motion-safe",
              )}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-biz-btn bg-biz-primary-subtle text-biz-primary-subtle-fg transition-colors group-hover:bg-biz-primary group-hover:text-biz-primary-foreground">
                <Icon className="h-5 w-5" weight="regular" aria-hidden />
              </span>
              <span className="text-[11px] font-semibold leading-tight text-biz-foreground">
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
